import React, { useCallback, useMemo, useState, type ReactNode } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';

import { retryTransientFailure } from '@/utils/query-retry';

type ClientSecretResponse = { client_secret: string };

class ClientSecretError extends Error {
  constructor(readonly status: number) {
    super(`Failed to fetch client secret (${status})`);
  }
}

const fetcher = async (url: string): Promise<ClientSecretResponse> => {
  const response = await fetch(url);

  // A throttled response is plain text, so parsing it as JSON would surface a
  // syntax error instead of something the user can act on.
  if (!response.ok) {
    throw new ClientSecretError(response.status);
  }

  return response.json();
};

type StripeWrapperProps = {
  stripe: { clientSecretPath: string; publishableKey: string };
  children: (clientSecret: string, refetchClientSecret: () => Promise<ClientSecretResponse | undefined>) => ReactNode;
};

function StripeForm({ stripe, children }: StripeWrapperProps) {
  // Each fetch mints a fresh Stripe SetupIntent, so a secret already fetched
  // is never refetched on its own (focus, reconnect): it would orphan one each
  // time. The forms ask for a new one when they have used it. A failed request
  // minted none, so it is retried.
  const { data, error, refetch } = useQuery<ClientSecretResponse, Error>({
    queryKey: ['stripeClientSecret', stripe.clientSecretPath],
    queryFn: () => fetcher(stripe.clientSecretPath),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: retryTransientFailure,
  });
  const refetchClientSecret = useCallback(async () => (await refetch()).data, [refetch]);

  // oxlint-disable-next-line react-hooks/exhaustive-deps -- Stripe is loaded once per mount, whatever the key prop does
  const stripePromise = useMemo(() => loadStripe(stripe.publishableKey), []);

  if (error) {
    return error instanceof ClientSecretError && error.status === 429 ? (
      <div>Too many attempts. Please wait a few minutes, then reload this page.</div>
    ) : (
      <div>Error loading payment form. Please try again or contact us for support.</div>
    );
  }

  if (!data) {
    return <div>Loading form....</div>;
  }

  return <Elements stripe={stripePromise}>{children(data.client_secret, refetchClientSecret)}</Elements>;
}

// The registration form is its own island on a Rails page, outside the
// dashboard's cache, so each mount carries one.
function StripeWrapper(props: StripeWrapperProps) {
  const [queryClient] = useState(() => new QueryClient());
  return (
    <QueryClientProvider client={queryClient}>
      <StripeForm {...props} />
    </QueryClientProvider>
  );
}

export { StripeWrapper };
